// src/tabs/IncidentsTab.jsx — onglet incidents admin (LOT Incidents 13).
//
// UNIFICATION : lit/écrit la table Supabase V2 unique `incidents` (plus de
// KV fbz8-incidents côté admin — la clé KV est conservée mais n'est plus
// utilisée ici). 3 sous-onglets alignés sur les 3 statuts canoniques :
//   - En cours    (status = en_cours)
//   - À racheter  (status = a_racheter)
//   - Résolu      (status = resolu)
//
// Création admin "+ Incident" → createIncident(source='admin_manual',
// status=en_cours). INSERT client direct sur la table `incidents` (PAS un
// endpoint requireAdmin) : la garantie est la RLS admin_all (Pattern B).
//
// Filtrage logIds (cohérent avec StockTab) :
//   - admin : tous logements
//   - ménage : ce composant est admin-only (App.jsx route les ménages vers
//     MenageIncidentsView) ; le garde isAdmin reste par sécurité.
//
// DA (DESIGN.md §8 — Niveau 1 board/cartes) : habillage seulement. Tokens
// marine/ciel/corail + sémantique (en_cours=warning · resolu=success ·
// a_racheter=danger) ; emojis → icônes lucide ; cartes `.fbz-card` ; l'état
// vit sur la pastille de statut (pas de liséré d'alerte sur la carte).
// AUCUNE logique/donnée/handler/machine à états touchée — l'argent
// (aircoverMontant) reste affiché à l'identique, jamais modifié ici.

import { useState, useEffect, useCallback, useMemo } from "react"
import {
  Plus, RefreshCw, AlertTriangle, Pencil, Trash2, User, CalendarDays,
  ClipboardList, ShoppingCart, CircleCheck, RotateCcw,
  FileText, ShieldCheck,
} from "lucide-react"
import { DS } from "../lib/ui"
import CarteTitrePage, { boutonCarteTitre } from "../components/CarteTitrePage"
import { fmtE } from "../lib/format"
import { MENAGE_STATUS_COLOR } from "../lib/domain-constants"
import { getDegs, incAircoverTotal, needsEtatFinancierQualif } from "../lib/incidents-helpers"
import { Modal } from "../components/Modal"
import IncidentModal from "../modals/IncidentModal"
import { supabase } from "../supabase"
import {
  STATUS,
  listIncidents,
  createIncident,
  deleteIncident,
  markBought,
  markResolved,
  markToBuy,
  computeRachatAutoPatch,
  normalizeStatus,
  statusMeta,
  fetchIncidentPhotos,
} from "../lib/incidents-api"
import { IncidentTimeline, RequalifBadge } from "../components/IncidentTimeline"
import { getAllLogsFlat, resolveLogementNom } from "../lib/sousloc-helpers"

// patch ciblé pour l'édition (champs éditables admin : degradations,
// voyageur, incident_date). Le statut est géré par les boutons d'action
// dédiés (markToBuy / markResolved / markBought), pas par cet UPDATE.
async function patchIncidentFields(id, patch) {
  const { error } = await supabase.from("incidents").update(patch).eq("id", id)
  if (error) throw error
}

// ── DA : habillage d'état par statut (présentationnel only) ──────────
// La sémantique reste la DONNÉE (normalizeStatus / statusMeta.label, hors
// scope). Ici on ne fait que choisir teinte + icône lucide d'affichage :
// en_cours = warning (en attente) · a_racheter = danger · resolu = success.
const STATUS_DA = {
  [STATUS.EN_COURS]:   { Icon: ClipboardList, color: DS.warning, bg: DS.warningBg, fg: DS.warningText },
  [STATUS.A_RACHETER]: { Icon: ShoppingCart,  color: DS.danger,  bg: DS.dangerBg,  fg: DS.dangerText },
  [STATUS.RESOLU]:     { Icon: CircleCheck,   color: DS.success, bg: DS.successBg, fg: DS.successText },
}
const statusDA = s => STATUS_DA[normalizeStatus(s)] || STATUS_DA[STATUS.EN_COURS]

// REFONTE 2026-08-13 — LA PAGE EST EN GROUPES, PLUS EN ONGLETS : tout se voit
// d'un regard, l'urgent d'abord (même doctrine que la vue agent/orchestrateur).
const GROUPES = [
  [STATUS.EN_COURS, "À juger", ClipboardList],
  [STATUS.A_RACHETER, "À racheter", ShoppingCart],
  [STATUS.RESOLU, "Réglés", CircleCheck],
]

export default function IncidentsTab({ cfg, currentUser, data, isAdmin, onIncidentsChanged }) {
  // Univers des logements = les FEUILLES (chambres sous-loc incluses, parent
  // Maison Zen exclu), comme IncidentModal qui ouvre la saisie depuis cet
  // écran. Avant : `cfg.props.flatMap(p => p.logs)` ne descendait pas dans
  // `log.chambres` — la liste proposait donc l'inverse de ce que la modale
  // offrait, et un incident sur lc1 s'affichait « lc1 » au lieu de son nom.
  const allLogsRaw = getAllLogsFlat(cfg)
  const allLogs = isAdmin
    ? allLogsRaw
    : allLogsRaw.filter(l => (currentUser?.logIds || []).includes(l.id))

  const [modal, setModal] = useState(null)         // 'new' | incident | null
  const [confirm, setConfirm] = useState(null)      // id à supprimer
  const [actionErr, setActionErr] = useState(null)

  // ── Chargement V2 ──────────────────────────────────────────────────
  const [incidents, setIncidents] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const reload = useCallback(async () => {
    if (!isAdmin) return
    setLoading(true); setError(null)
    try {
      const all = await listIncidents({ limit: 200 })
      setIncidents(all)
    } catch (e) { setError(e.message || String(e)) }
    finally { setLoading(false) }
  }, [isAdmin])

  useEffect(() => { reload() }, [reload])

  // LOT Incidents 13 — propagation vers App.jsx après une écriture réussie : les
  // AUTRES lecteurs du state `incidents` (facture proprio, dashboard, alerte
  // proprio) sont rafraîchis sans reload de page. Le reload LOCAL (ci-dessus)
  // reste intact ; on ajoute seulement la notification au parent.
  const notifyParent = useCallback(() => {
    if (typeof onIncidentsChanged === "function") {
      Promise.resolve(onIncidentsChanged()).catch(e => console.error("[incidents] notifyParent failed", e))
    }
  }, [onIncidentsChanged])

  const parGroupe = useMemo(() => {
    const g = { [STATUS.EN_COURS]: [], [STATUS.A_RACHETER]: [], [STATUS.RESOLU]: [] }
    incidents.forEach(i => { g[normalizeStatus(i.status)].push(i) })
    return g
  }, [incidents])

  // PRIX HABITUELS par type (table incident_types_prix, RLS admin seul) —
  // lecture seule ici ; l'édition vit dans Paramètres → « Prix des
  // remplacements ». Best-effort : sans la table, aucun prix ne s'affiche.
  const [prixTypes, setPrixTypes] = useState({})
  useEffect(() => {
    if (!isAdmin) return
    let annule = false
    ;(async () => {
      try {
        const { data: rows, error: e } = await supabase
          .from('incident_types_prix').select('type, prix_eur')
        if (e) throw e
        if (!annule) setPrixTypes(Object.fromEntries((rows || []).map(r => [r.type, Number(r.prix_eur)])))
      } catch (e) { console.error('[incidents] prix types', e?.message || e) }
    })()
    return () => { annule = true }
  }, [isAdmin])

  // Résolution du nom sur l'arbre COMPLET (parents + chambres) : une donnée
  // historique peut porter l'id d'un parent, absent de getAllLogsFlat.
  const logName = useCallback(lid => resolveLogementNom(cfg, lid), [cfg])

  // ── Création / édition via IncidentModal (mappé vers V2) ───────────
  // IncidentModal.onSave renvoie un objet KV-shaped :
  //   { id, propId, logId, voyageur, date, superhote, degradations, photos? }
  // On le mappe vers la table V2 (createIncident pour un nouvel incident).
  async function saveIncident(form) {
    setActionErr(null)
    // Garde-fou B3 (aligné sur createIncident + trigger DB) : un incident DOIT
    // comporter au moins une dégradation. On refuse plutôt que d'écrire `[]` muet
    // (à la création comme à l'édition — jamais de degradations vide en base).
    if (!Array.isArray(form.degradations) || form.degradations.length === 0) {
      setActionErr("Au moins une dégradation est requise.")
      return
    }
    try {
      if (!form.id) {
        // Nouveau → INSERT V2, source admin_manual, statut initial en_cours
        const firstType = (form.degradations?.[0]?.type) || null
        await createIncident({
          logement_id: form.logId,
          source: "admin_manual",
          status: STATUS.EN_COURS,
          degradations: form.degradations || [],
          voyageur: form.voyageur || null,
          casse_interne: !!form.casse_interne,
          incident_date: form.date || null,
          label: firstType,
          reported_by: currentUser?.email || "admin",
          photos: Array.isArray(form.photos) ? form.photos : [],
        })
      } else {
        // Édition d'un incident V2 existant : on ne ré-écrit que les champs
        // éditables côté admin via patch ciblé (statut géré par les boutons
        // d'action dédiés, pas ici).
        const patch = {
          degradations: form.degradations || [],
          voyageur: form.voyageur || null,
          casse_interne: !!form.casse_interne,
          incident_date: form.date || null,
        }

        // REFONTE Lot 5 — RACHAT AUTO (bonus, en plus du bouton « Marquer racheté »).
        //   Si l'incident COURANT est « à racheter » (OLD.status, lu sur `modal`
        //   qui porte la ligne V2 — la KV-shape du form ne porte PAS le statut) ET
        //   qu'il existe ≥1 dégradation « Non récupérable » ET que TOUTES ces
        //   dégradations « Non récupérable » ont leur date « À remplacer »
        //   (remplaceLe) remplie → l'incident bascule automatiquement en « résolu »,
        //   bought_date = max(remplaceLe). Admin = bypass trigger (patch direct).
        //   La décision (gardes incluses) vit dans le moteur pur computeRachatAutoPatch.
        //   markBought reste la clôture manuelle conservée (bouton « Marquer racheté »).
        const oldStatus = modal && typeof modal === "object" ? normalizeStatus(modal.status) : null
        const rachatAuto = computeRachatAutoPatch({
          oldStatus,
          degradations: form.degradations || [],
          adminEmail: currentUser?.email || "admin",
        })
        if (rachatAuto) Object.assign(patch, rachatAuto)

        await patchIncidentFields(form.id, patch)
      }
      setModal(null)
      await reload()
      notifyParent()
    } catch (e) {
      setActionErr(e.message || String(e))
    }
  }

  async function handleDelete(id) {
    setActionErr(null)
    try {
      await deleteIncident(id)
      setConfirm(null)
      await reload()
      notifyParent()
    } catch (e) {
      setActionErr(e.message || String(e))
      setConfirm(null)
    }
  }

  // Actions de transition admin (boutons par carte)
  async function doTransition(fn, label) {
    setActionErr(null)
    try { await fn(); await reload(); notifyParent() }
    catch (e) { setActionErr(`${label} : ${e.message || String(e)}`) }
  }

  // Pour l'édition : IncidentModal attend la forme KV. On reconstruit un
  // incident KV-shaped depuis la ligne V2 pour pré-remplir la modale.
  const v2ToKvShape = inc => ({
    id: inc.id,
    logId: inc.logement_id,
    voyageur: inc.voyageur || "",
    date: inc.incident_date || (inc.reported_at ? inc.reported_at.split("T")[0] : ""),
    superhote: false,
    degradations: Array.isArray(inc.degradations) ? inc.degradations : [],
  })

  return (
    <div style={{fontFamily:DS.fontBody}}>
      {confirm && (
        <Modal
          title="Supprimer l'incident ?"
          msg="Cette action est irr&#233;versible."
          onOk={() => handleDelete(confirm)}
          onCancel={() => setConfirm(null)}
        />
      )}
      {modal && (
        <IncidentModal
          incident={modal === "new" ? null : v2ToKvShape(modal)}
          cfg={cfg}
          data={data}
          onSave={saveIncident}
          onClose={() => setModal(null)}
          defaultLogId={modal === "new" ? (allLogs[0]?.id || "") : undefined}
          isAdmin={currentUser?.role === "admin"}
          userLogIds={currentUser?.logIds || []}
        />
      )}

      {/* Carte de titre (lot apparence 2026-08-14) — le bouton passe en blanc :
          dans la carte, les boutons sont blancs sur ciel pâle ; le corail est
          désormais réservé aux ALERTES, et créer un incident n'en est pas une. */}
      <CarteTitrePage
        titre="Incidents"
        actions={
          <button className="fbz-press fbz-tap" onClick={() => setModal("new")}
            style={boutonCarteTitre()}>
            <Plus size={16} strokeWidth={2.4}/> Incident
          </button>
        } />

      {/* Toolbar */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12,gap:10,flexWrap:"wrap"}}>
        <div style={{fontSize:12,color:DS.textSecondary}}>
          {loading ? "Chargement…" : `${incidents.length} incident${incidents.length > 1 ? "s" : ""}`}
        </div>
        <button className="fbz-press" onClick={reload} disabled={loading}
          style={{display:"inline-flex",alignItems:"center",gap:5,background:DS.card,color:DS.marine,border:`0.5px solid ${DS.borderHover}`,borderRadius:DS.radiusControl,padding:"7px 12px",fontSize:12,fontWeight:600,cursor:loading?"not-allowed":"pointer",fontFamily:DS.fontBody,opacity:loading?0.5:1}}>
          <RefreshCw size={14} strokeWidth={2.2}/> Recharger
        </button>
      </div>

      {error && (
        <div style={{display:"flex",gap:8,alignItems:"flex-start",padding:"9px 12px",background:DS.dangerBg,color:DS.dangerText,borderRadius:8,borderLeft:`3px solid ${DS.danger}`,fontSize:12.5,marginBottom:12}}>
          <AlertTriangle size={15} strokeWidth={2.2} style={{flexShrink:0,marginTop:1}}/> <span>{error}</span>
        </div>
      )}
      {actionErr && (
        <div style={{display:"flex",gap:8,alignItems:"flex-start",padding:"9px 12px",background:DS.dangerBg,color:DS.dangerText,borderRadius:8,borderLeft:`3px solid ${DS.danger}`,fontSize:12.5,marginBottom:12}}>
          <AlertTriangle size={15} strokeWidth={2.2} style={{flexShrink:0,marginTop:1}}/> <span>{actionErr}</span>
        </div>
      )}

      {!loading && incidents.length === 0 && (
        <div style={{background:DS.card,borderRadius:DS.radiusCard,padding:"40px 16px",textAlign:"center",border:`0.5px solid ${DS.border}`}}>
          <div style={{width:56,height:56,borderRadius:16,background:DS.cielSoft,display:"inline-flex",alignItems:"center",justifyContent:"center",marginBottom:12}}>
            <CircleCheck size={26} strokeWidth={1.8} color={DS.marine}/>
          </div>
          <div style={{fontSize:15,fontWeight:600,color:DS.marineDark,fontFamily:DS.fontDisplay}}>Aucun incident</div>
        </div>
      )}

      {/* ═══ LES GROUPES — l'urgent d'abord, tout d'un regard ═══ */}
      {GROUPES.map(([cle, titre, GroupIco]) => {
        const liste = parGroupe[cle]
        if (!liste.length) return null
        const da = STATUS_DA[cle]
        return (
          <section key={cle} data-testid={`admin-incidents-groupe-${cle}`} style={{marginBottom:18}}>
            <div style={{display:"flex",alignItems:"center",gap:6,padding:"5px 2px",marginBottom:8}}>
              <GroupIco size={13} strokeWidth={2.2} color={da.fg} />
              <span style={{fontSize:11,fontWeight:700,color:da.fg,textTransform:"uppercase",letterSpacing:".07em"}}>
                {titre} · {liste.length}
              </span>
            </div>
            {liste.map(inc => (
              <IncidentCard
                key={inc.id}
                inc={inc}
                logName={logName(inc.logement_id)}
                prixTypes={prixTypes}
                onEdit={() => setModal(inc)}
                onDelete={() => setConfirm(inc.id)}
                onMarkToBuy={() => doTransition(() => markToBuy(inc.id), "À racheter")}
                onMarkResolved={() => doTransition(() => markResolved(inc.id), "Résolu")}
                onMarkBought={() => doTransition(
                  () => markBought(inc.id, { bought_by: currentUser?.email || "admin" }),
                  "Racheté",
                )}
                onReopen={() => doTransition(() => patchIncidentFields(inc.id, { status: STATUS.EN_COURS }), "Ré-ouvert")}
              />
            ))}
          </section>
        )
      })}

      {/* ═══ COMPTA — trois cartes : ce mois / par logement / par type ═══
          Le mois d'un remplacement = sa date de DÉPÔT (remplaceLe), comme la
          facture. Coûts FULLBNBZEN + refacturés confondus : c'est le suivi des
          remplacements, pas un compte de résultat. ADMIN SEUL (ce composant
          n'est jamais monté pour un autre rôle). */}
      <ComptaRemplacements incidents={incidents} logName={logName} />

      <div style={{display:"flex",gap:8,alignItems:"flex-start",marginTop:8,fontSize:11.5,color:DS.textMuted,lineHeight:1.5,padding:"10px 12px",background:DS.pageBg,borderRadius:8,border:`0.5px solid ${DS.border}`}}>
        <ShieldCheck size={14} strokeWidth={2} style={{flexShrink:0,marginTop:1}}/>
        {/* Micro-lot admin (2026-08-01) — la note technique (table Supabase,
            statuts canoniques) était de la doc développeur fuitée dans l'UI :
            remplacée par la phrase métier. Le détail technique vit en tête de
            fichier et dans lib/incidents-api.js. */}
        <span>Les incidents déclarés par l'équipe ménage apparaissent ici.</span>
      </div>
    </div>
  )
}

// ── IncidentCard ─────────────────────────────────────────────────────

function IncidentCard({ inc, logName, prixTypes, onEdit, onDelete, onMarkToBuy, onMarkResolved, onMarkBought, onReopen }) {
  const status = normalizeStatus(inc.status)
  const meta = statusMeta(inc.status)        // label canonique (donnée, hors scope DA)
  const da = statusDA(inc.status)            // teinte + icône d'affichage (présentationnel)
  const StatusIcon = da.Icon
  const degs = getDegs(inc)
  // Lot 5 — timeline admin (déclaration + chaque changement de statut) + badge
  // « requalifié par {responsable} ». Chargée une fois, partagée badge/timeline.
  const [timeline, setTimeline] = useState(null)
  // Lot 2 photo (ADR-209) : photos d'incident sur bucket PRIVÉ → signed URL via
  // l'endpoint (legacy public = pass-through). Fallback brut si échec/chargement.
  const [signed, setSigned] = useState(null)
  const [signFailed, setSignFailed] = useState(false)
  useEffect(() => {
    let cancelled = false
    setSignFailed(false)
    const raw = Array.isArray(inc.photos) ? inc.photos : []
    if (!raw.length) { setSigned([]); return }
    // Échec de signature → s = null : on GARDE le repli inc.photos (ne jamais
    // masquer des photos existantes derrière un []).
    ;(async () => {
      const s = await fetchIncidentPhotos(inc.id)
      if (cancelled) return
      if (s) setSigned(s)
      else setSignFailed(true)
    })()
    return () => { cancelled = true }
  }, [inc.id, inc.photos])
  const photos = signed || (Array.isArray(inc.photos) ? inc.photos : [])
  const aircover = incAircoverTotal(inc)
  // Source = repère de provenance (pas un état) : admin = ardoise, ménage = ciel.
  const sourceBadge = inc.source === "admin_manual"
    ? { bg: DS.catArdoiseBg, fg: DS.catArdoiseIco, Icon: User, label: "Admin" }
    : { bg: DS.cielSoft, fg: DS.marine, Icon: CalendarDays, label: "Ménage" }
  const SourceIcon = sourceBadge.Icon

  return (
    <div className="fbz-card" style={{
      background:DS.card,borderRadius:DS.radiusCard,padding:15,marginBottom:10,
      border:`0.5px solid ${DS.border}`,
    }}>
      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:10,flexWrap:"wrap",marginBottom:10}}>
        <div>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4,flexWrap:"wrap"}}>
            <span style={{display:"inline-flex",alignItems:"center",gap:5,background:da.bg,color:da.fg,borderRadius:7,padding:"3px 9px",fontSize:10.5,fontWeight:700,letterSpacing:.3,textTransform:"uppercase"}}>
              <StatusIcon size={12} strokeWidth={2.4}/> {meta.label}
            </span>
            <span style={{display:"inline-flex",alignItems:"center",gap:4,background:sourceBadge.bg,color:sourceBadge.fg,borderRadius:7,padding:"3px 9px",fontSize:10.5,fontWeight:700}}>
              <SourceIcon size={12} strokeWidth={2.2}/> {sourceBadge.label}
            </span>
            {timeline && <RequalifBadge timeline={timeline} />}
            {/* Chantier ⑥ — indicateur « à qualifier » : une dégradation Non
                récupérable sans état financier renseigné (enjeu argent en attente). */}
            {needsEtatFinancierQualif(inc) && (
              <span style={{display:"inline-flex",alignItems:"center",gap:4,background:DS.warningBg,color:DS.warningText,borderRadius:7,padding:"3px 9px",fontSize:10.5,fontWeight:700,letterSpacing:.3,textTransform:"uppercase",border:`1px solid ${DS.warning}55`}}>
                ⚠️ À qualifier
              </span>
            )}
            <span style={{fontSize:11,color:DS.textMuted}}>
              {logName} · {inc.incident_date || (inc.reported_at ? new Date(inc.reported_at).toLocaleDateString("fr-FR") : "—")}
            </span>
          </div>
          {inc.voyageur && (
            <div style={{display:"inline-flex",alignItems:"center",gap:5,fontSize:12,color:DS.textSecondary}}><User size={13} strokeWidth={2}/> {inc.voyageur}</div>
          )}
          {inc.label && (
            <div style={{fontSize:14.5,fontWeight:700,color:DS.marineDark,marginTop:2}}>{inc.label}</div>
          )}
          {inc.description && (
            <div style={{fontSize:12,color:DS.textSecondary,marginTop:4,whiteSpace:"pre-wrap"}}>{inc.description}</div>
          )}
          {inc.reported_by && (
            <div style={{fontSize:10.5,color:DS.textMuted,marginTop:4}}>Signalé par {inc.reported_by}</div>
          )}
        </div>
        <div style={{display:"flex",gap:4,whiteSpace:"nowrap"}}>
          <button className="fbz-press fbz-tap" onClick={onEdit} title="Modifier"
            style={{display:"inline-flex",alignItems:"center",justifyContent:"center",width:30,height:30,background:DS.cielSoft,color:DS.marine,border:"none",borderRadius:8,cursor:"pointer"}}>
            <Pencil size={15} strokeWidth={2}/>
          </button>
          <button className="fbz-press fbz-tap" onClick={onDelete} title="Supprimer"
            style={{display:"inline-flex",alignItems:"center",justifyContent:"center",width:30,height:30,background:DS.dangerBg,color:DS.danger,border:"none",borderRadius:8,cursor:"pointer"}}>
            <Trash2 size={15} strokeWidth={2}/>
          </button>
        </div>
      </div>

      {/* Dégradations */}
      {degs.length > 0 && (
        <div style={{padding:"10px 12px",background:DS.pageBg,borderRadius:9,marginBottom:10,border:`0.5px solid ${DS.border}`}}>
          {degs.map((d, di) => {
            const sc = MENAGE_STATUS_COLOR[d.statutMenage] || DS.textMuted
            const prixHabituel = prixTypes && d.type != null ? prixTypes[d.type] : null
            return (
              <div key={d.id || di} style={{marginBottom:di<degs.length-1?8:0}}>
                <div style={{display:"flex",alignItems:"center",gap:5,flexWrap:"wrap"}}>
                  <span style={{background:DS.dangerBg,color:DS.dangerText,borderRadius:6,padding:"2px 8px",fontSize:10.5,fontWeight:700}}>{d.type}</span>
                  {Number(d.nombre) > 1 && <span style={{background:DS.cielSoft,color:DS.marine,borderRadius:6,padding:"2px 7px",fontSize:10.5,fontWeight:700}}>×{d.nombre}</span>}
                  {d.precision && <span style={{fontSize:10.5,color:DS.textSecondary,fontStyle:"italic"}}>{d.precision}</span>}
                  {d.statutMenage && <span style={{background:sc+"22",color:sc,borderRadius:6,padding:"2px 7px",fontSize:9.5,fontWeight:700}}>{d.statutMenage}</span>}
                  {/* SOURCE DE REMBOURSEMENT TYPÉE — l'un des 5 états, en clair. */}
                  {d.etatFinancier && (
                    <span style={{background:DS.toolTagBg,color:DS.marine,borderRadius:6,padding:"2px 7px",fontSize:9.5,fontWeight:700}}>{d.etatFinancier}</span>
                  )}
                  {/* PRIX HABITUEL (lecture seule — s'édite dans Paramètres). */}
                  {d.statutMenage === "Non récupérable" && !d.remplaceLe && Number.isFinite(prixHabituel) && (
                    <span title="Prix habituel de remplacement (Paramètres → Prix des remplacements)"
                      style={{fontSize:9.5,color:DS.textMuted,fontWeight:600}}>prix habituel : {prixHabituel.toFixed(0)} €</span>
                  )}
                </div>
                {/* LA LIGNE DU DÉPÔT — chez l'ADMIN, avec le nom et l'argent :
                    « Déposé le 14 août par Dimitri » puis, en plus petit,
                    « 36 € · refacturé au propriétaire · remboursé par AirCover ». */}
                {(d.racheteLe || d.remplaceLe) && (
                  <div style={{marginTop:3,fontSize:10.5,color:DS.textSecondary,lineHeight:1.5}}>
                    {d.remplaceLe ? (
                      <span style={{fontWeight:700,color:DS.successText}}>
                        Déposé le {new Date(d.remplaceLe + "T12:00:00").toLocaleDateString("fr-FR", { day: "numeric", month: "long" })}
                        {d.deposePar ? ` par ${d.deposePar.split("@")[0]}` : ""}
                      </span>
                    ) : (
                      <span style={{fontWeight:700,color:DS.marine}}>
                        Racheté le {new Date(d.racheteLe + "T12:00:00").toLocaleDateString("fr-FR", { day: "numeric", month: "long" })}
                        {d.rachetePar ? ` par ${d.rachetePar.split("@")[0]}` : ""} — à déposer
                      </span>
                    )}
                    <span style={{display:"block",fontSize:9.5,color:DS.textMuted}}>
                      {[
                        parseFloat(d.cout) > 0 ? `${parseFloat(d.cout).toFixed(0)} €` : null,
                        d.etatFinancier ? d.etatFinancier.toLowerCase() : null,
                        parseFloat(d.aircoverMontant) > 0 ? `AirCover proprio ${parseFloat(d.aircoverMontant).toFixed(0)} €` : null,
                        parseFloat(d.aircoverFbz) > 0 ? `AirCover FBZ ${parseFloat(d.aircoverFbz).toFixed(0)} €` : null,
                      ].filter(Boolean).join(" · ")}
                    </span>
                  </div>
                )}
                {!d.remplaceLe && parseFloat(d.aircoverMontant) > 0 && (
                  <span style={{background:DS.cielSoft,color:DS.marine,borderRadius:6,padding:"2px 7px",fontSize:9.5,fontWeight:700,display:"inline-block",marginTop:3}}>Aircover : {parseFloat(d.aircoverMontant).toFixed(2)} €</span>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Photos */}
      {signFailed && (
        <div style={{fontSize:10.5,color:DS.textMuted,fontStyle:"italic",marginBottom:6}}>
          Photos sécurisées momentanément indisponibles.
        </div>
      )}
      {photos.length > 0 && (
        <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:10}}>
          {photos.map((p, i) => {
            // Photo null ou sans url → rien (garde alignée sur ResaV2Modal ;
            // piège : typeof null === 'object').
            const url = typeof p === "string" ? p : (p && p.url)
            if (!url) return null
            return (
              <a key={i} href={url} target="_blank" rel="noopener noreferrer"
                style={{display:"block",border:`0.5px solid ${DS.border}`,borderRadius:8,overflow:"hidden"}}>
                <img src={url} alt={`preuve ${i+1}`}
                  style={{display:"block",width:80,height:80,objectFit:"cover"}}/>
              </a>
            )
          })}
        </div>
      )}

      {/* Timestamps résolution */}
      {(inc.recovered_at || inc.bought_at) && (
        <div style={{padding:"8px 11px",background:DS.pageBg,borderRadius:8,fontSize:11.5,color:DS.textSecondary,lineHeight:1.6,marginBottom:8,border:`0.5px solid ${DS.border}`}}>
          {inc.recovered_at && (
            <div style={{display:"inline-flex",alignItems:"center",gap:5}}><CircleCheck size={13} strokeWidth={2} color={DS.success}/> Récupéré le {new Date(inc.recovered_at).toLocaleString("fr-FR", { day:"2-digit",month:"2-digit",hour:"2-digit",minute:"2-digit" })}{inc.recovered_by ? ` par ${inc.recovered_by}` : ""}</div>
          )}
          {inc.bought_at && (
            <div style={{display:"flex",alignItems:"center",gap:5}}><ShoppingCart size={13} strokeWidth={2} color={DS.marine}/> Racheté le {inc.bought_date ? new Date(inc.bought_date + "T12:00:00").toLocaleDateString("fr-FR") : new Date(inc.bought_at).toLocaleDateString("fr-FR")}{inc.bought_by ? ` (saisi par ${inc.bought_by})` : ""}</div>
          )}
        </div>
      )}

      {/* Note admin */}
      {inc.admin_note && (
        <div style={{display:"flex",gap:7,alignItems:"flex-start",padding:"8px 11px",background:DS.cielSoft,borderRadius:8,fontSize:12,color:DS.marineDark,marginBottom:8,whiteSpace:"pre-wrap"}}>
          <FileText size={14} strokeWidth={2} style={{flexShrink:0,marginTop:1}}/> <span>{inc.admin_note}</span>
        </div>
      )}

      {aircover > 0 && (
        <div style={{fontSize:12.5,fontWeight:700,color:DS.marineDark,marginBottom:8}}>Aircover total : {fmtE(aircover)}</div>
      )}

      {/* DOSSIER AIRCOVER (GO Dimitri 2026-08-13) — ADMIN SEUL : réclamation
          IA-rédigée ÉPHÉMÈRE (jamais envoyée, jamais persistée — à copier vers
          Airbnb) + téléchargement des photos nommées. */}
      <DossierAirCover inc={inc} logName={logName} photos={photos} />

      {/* Lot 5 — historique admin (déclaration + changements de statut, qui/quand)
          + badge requalif. Lecture seule, dérivée d'audit_log (status only, 0 PII). */}
      <IncidentTimeline incidentId={inc.id} onLoaded={setTimeline} />

      {/* Actions de transition admin selon statut courant */}
      <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
        {status === STATUS.EN_COURS && (
          <>
            <ActionButton color={DS.danger} Icon={ShoppingCart} label="À racheter" onClick={onMarkToBuy}/>
            <ActionButton color={DS.success} Icon={CircleCheck} label="Résolu" onClick={onMarkResolved}/>
          </>
        )}
        {status === STATUS.A_RACHETER && (
          <ActionButton color={DS.success} Icon={ShoppingCart} label="Marquer racheté" onClick={onMarkBought}/>
        )}
        {/* RÉ-OUVRIR — réactivé (refonte 2026-08-13) : le bouton était mort
            (onClick=null). Un incident clos par erreur redevient « à juger » ;
            l'audit_log trace le retour comme toute transition. */}
        {status === STATUS.RESOLU && (
          <ActionButton color={DS.textMuted} Icon={RotateCcw} label="Ré-ouvrir" onClick={onReopen}
            title="Repasser l'incident en « à juger »"/>
        )}
      </div>
    </div>
  )
}

function ActionButton({ color, Icon, label, onClick, disabled, title }) {
  return (
    <button className={disabled ? undefined : "fbz-press fbz-tap"} onClick={onClick || undefined} disabled={disabled} title={title}
      style={{
        background: disabled ? DS.pageBg : color, color: disabled ? DS.textMuted : "#fff",
        border: disabled ? `0.5px solid ${DS.border}` : "none", borderRadius:DS.radiusControl, padding:"7px 13px",
        fontSize:11.5,fontWeight:700,cursor:disabled?"not-allowed":"pointer",
        fontFamily:DS.fontBody,
        display:"inline-flex",alignItems:"center",gap:5,
      }}>
      {Icon && <Icon size={14} strokeWidth={2.2}/>} {label}
    </button>
  )
}


// ── DOSSIER AIRCOVER — réclamation éphémère + photos nommées (admin seul) ────
//
// ⚠️ RIEN NE PART D'ICI ET RIEN NE SE GARDE : le texte est GÉNÉRÉ à la demande
// (gabarit déterministe FR/EN — pas d'appel IA : le contenu est entièrement
// factuel, un LLM n'y ajouterait que du risque), affiché pour COPIE vers le
// formulaire Airbnb, puis perdu à la fermeture. Les photos se téléchargent
// NOMMÉES (logement-article-n) pour un dossier lisible côté AirCover.
function DossierAirCover({ inc, logName, photos }) {
  const [ouvert, setOuvert] = useState(false)
  const [langue, setLangue] = useState('fr')
  const [copie, setCopie] = useState(false)
  const degs = getDegs(inc).filter(d => d.statutMenage === 'Non récupérable')
  if (!degs.length || inc.casse_interne) return null

  const dateFr = inc.incident_date
    ? new Date(inc.incident_date + 'T12:00:00').toLocaleDateString(langue === 'fr' ? 'fr-FR' : 'en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
    : '—'
  const lignes = degs.map(d => {
    const qte = Number(d.nombre) > 1 ? `${d.nombre}× ` : ''
    const cout = parseFloat(d.cout) > 0 ? ` — ${parseFloat(d.cout).toFixed(2)} €` : ''
    return `• ${qte}${d.type}${d.precision ? ` (${d.precision})` : ''}${cout}`
  }).join('\n')
  const total = degs.reduce((s2, d) => s2 + (parseFloat(d.cout) > 0 ? parseFloat(d.cout) : 0), 0)

  const texte = langue === 'fr'
    ? `Bonjour,\n\nLors du séjour${inc.voyageur ? ` de ${inc.voyageur}` : ''} dans notre logement « ${logName} », notre équipe de ménage a constaté le ${dateFr} les dégradations suivantes :\n\n${lignes}\n\nCes articles sont non récupérables et ont dû être remplacés${total > 0 ? ` (coût total : ${total.toFixed(2)} €)` : ''}. Les photos jointes, prises lors du constat, documentent chaque dégradation.\n\nNous sollicitons une prise en charge au titre d'AirCover.\n\nCordialement,\nFULLBNBZEN`
    : `Hello,\n\nDuring ${inc.voyageur ? `${inc.voyageur}'s stay` : 'a recent stay'} in our listing "${logName}", our cleaning team documented the following damage on ${dateFr}:\n\n${lignes}\n\nThese items are beyond repair and had to be replaced${total > 0 ? ` (total cost: €${total.toFixed(2)})` : ''}. The attached photos, taken during the inspection, document each damaged item.\n\nWe are requesting reimbursement under AirCover.\n\nBest regards,\nFULLBNBZEN`

  const slug = (t) => String(t || 'article').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')

  return (
    <div style={{marginBottom:8}}>
      <button type="button" className="fbz-press" data-testid={`aircover-ouvrir-${inc.id}`}
        onClick={() => setOuvert(v => !v)}
        style={{display:"inline-flex",alignItems:"center",gap:6,background:DS.cielSoft,color:DS.marine,border:"none",borderRadius:DS.radiusControl,padding:"7px 12px",fontSize:11.5,fontWeight:700,cursor:"pointer",fontFamily:DS.fontBody}}>
        <ShieldCheck size={14} strokeWidth={2.2}/> Dossier AirCover
      </button>
      {ouvert && (
        <div data-testid={`aircover-panneau-${inc.id}`}
          style={{marginTop:8,padding:"12px 13px",background:DS.card,border:`1px solid ${DS.border}`,borderRadius:10}}>
          <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:8}}>
            {['fr', 'en'].map(l => (
              <button key={l} type="button" onClick={() => setLangue(l)}
                style={{padding:"4px 10px",fontSize:11,fontWeight:700,borderRadius:7,cursor:"pointer",fontFamily:DS.fontBody,
                  border: langue === l ? "none" : `1px solid ${DS.border}`,
                  background: langue === l ? DS.ciel : "#fff", color: langue === l ? "#1c2833" : DS.textSecondary}}>
                {l.toUpperCase()}
              </button>
            ))}
            <button type="button" className="fbz-press"
              onClick={() => { navigator.clipboard?.writeText(texte).then(() => { setCopie(true); setTimeout(() => setCopie(false), 1600) }).catch(() => {}) }}
              style={{marginLeft:"auto",padding:"4px 10px",fontSize:11,fontWeight:700,borderRadius:7,border:"none",background:DS.marine,color:"#fff",cursor:"pointer",fontFamily:DS.fontBody}}>
              {copie ? 'Copié ✓' : 'Copier le texte'}
            </button>
          </div>
          <pre data-testid={`aircover-texte-${inc.id}`}
            style={{whiteSpace:"pre-wrap",fontSize:11.5,color:DS.textPrimary,fontFamily:DS.fontBody,background:DS.toolBg,borderRadius:8,padding:"10px 12px",margin:0,lineHeight:1.5}}>{texte}</pre>
          {photos.length > 0 && (
            <div style={{marginTop:8}}>
              <div style={{fontSize:10,fontWeight:700,color:DS.textMuted,textTransform:"uppercase",letterSpacing:".05em",marginBottom:5}}>
                Photos du dossier — nommées pour AirCover
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:3}}>
                {photos.map((ph, i) => {
                  const url = typeof ph === "string" ? ph : (ph && ph.url)
                  if (!url) return null
                  const nom = `${slug(logName)}-${slug(degs[0]?.type)}-${i + 1}.jpg`
                  return (
                    <a key={i} href={url} download={nom} target="_blank" rel="noopener noreferrer"
                      style={{fontSize:11.5,color:DS.marine,textDecoration:"underline"}}>
                      {nom}
                    </a>
                  )
                })}
              </div>
            </div>
          )}
          <div style={{marginTop:8,fontSize:10,color:DS.textMuted,fontStyle:"italic"}}>
            Rien n'est envoyé ni conservé : copiez le texte dans le formulaire Airbnb, téléchargez les photos.
          </div>
        </div>
      )}
    </div>
  )
}

// ── COMPTA — trois cartes : ce mois / par logement / par type ────────────────
// Le mois d'un remplacement = sa date de DÉPÔT (remplaceLe) — la même règle
// que la facture. On compte les coûts posés (cout > 0) des articles déposés.
function ComptaRemplacements({ incidents, logName }) {
  const lignes = useMemo(() => {
    const out = []
    for (const inc of incidents) {
      for (const d of getDegs(inc)) {
        const cout = parseFloat(d.cout)
        if (!d.remplaceLe || !(cout > 0)) continue
        out.push({ mois: String(d.remplaceLe).slice(0, 7), logement: inc.logement_id, type: d.type || '?', cout })
      }
    }
    return out
  }, [incidents])

  const moisCourant = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Paris' }).format(new Date()).slice(0, 7)
  const totalMois = lignes.filter(l => l.mois === moisCourant).reduce((s2, l) => s2 + l.cout, 0)
  const par = (cle) => {
    const m = new Map()
    lignes.forEach(l => m.set(l[cle], (m.get(l[cle]) || 0) + l.cout))
    return [...m.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5)
  }
  if (!lignes.length) return null

  const carte = { flex: '1 1 180px', background: DS.card, border: `0.5px solid ${DS.border}`, borderRadius: DS.radiusCard, padding: '12px 14px' }
  const titre = { fontSize: 10, fontWeight: 700, color: DS.textMuted, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }
  const ligne = { display: 'flex', justifyContent: 'space-between', fontSize: 11.5, color: DS.textSecondary, marginTop: 3 }

  return (
    <section data-testid="compta-remplacements" style={{marginTop:6,marginBottom:14}}>
      <div style={{fontSize:11,fontWeight:700,color:DS.marine,textTransform:"uppercase",letterSpacing:".07em",marginBottom:8}}>
        Remplacements — suivi
      </div>
      <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
        <div style={carte} data-testid="compta-mois">
          <div style={titre}>Ce mois</div>
          <div style={{fontFamily:DS.fontDisplay,fontSize:22,fontWeight:700,color:DS.marineDark}}>{fmtE(totalMois)}</div>
          <div style={{fontSize:10.5,color:DS.textMuted,marginTop:2}}>{lignes.filter(l => l.mois === moisCourant).length} article(s) déposé(s)</div>
        </div>
        <div style={carte} data-testid="compta-logement">
          <div style={titre}>Par logement</div>
          {par('logement').map(([lid, total]) => (
            <div key={lid} style={ligne}><span>{logName(lid)}</span><strong style={{color:DS.marineDark}}>{fmtE(total)}</strong></div>
          ))}
        </div>
        <div style={carte} data-testid="compta-type">
          <div style={titre}>Par type</div>
          {par('type').map(([t, total]) => (
            <div key={t} style={ligne}><span>{t}</span><strong style={{color:DS.marineDark}}>{fmtE(total)}</strong></div>
          ))}
        </div>
      </div>
    </section>
  )
}
